import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useHistory, useParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import {
  Accordion,
  AccordionItem,
  Button,
  ComboBox,
  DatePicker,
  DatePickerInput,
  InlineNotification,
  Modal,
  NumberInput,
  OverflowMenu,
  OverflowMenuItem,
  Select,
  SelectItem,
  TextInput,
} from '@carbon/react';
import { Add, Copy, Save, TrashCan } from '@carbon/react/icons';
import { EmptyState } from '@carbon/ibm-products';
import routes from '~/routes';
import { NavigationGuard } from 'components/navigation-guard/navigation-guard';
import { DEFAULT_COLORS } from 'components/color-picker';
import {
  PricingPageLayout,
  type PricingToast,
} from 'pages/pricing/shared/pricing-page-layout';
import {
  contractsAtom,
  curvesAtom,
  useRefreshContracts,
  useRefreshCurves,
} from 'pages/pricing/shared/atoms';
import { pricingApi } from 'pages/pricing/shared/api';
import type { MarketDataQuery, SavedView } from 'pages/pricing/shared/types';
import { PriceChart, type PricePlot } from './price-chart';
import { buildPricePlot, type SeriesMeta } from './price-plot';
import { SeriesEditor } from './series-editor';
import {
  type AsOfMode,
  type ViewDraft,
  type VizSeries,
  DEFAULT_HISTORICAL,
  DEFAULT_HORIZON,
  DEFAULT_VIEW_NAME,
  blankSeries,
  buildQuery,
  fmtDate,
  horizonEnd,
  nextId,
  resolveBase,
  snapshot,
} from './view-model';
import {
  ActivityFeedPanel,
  ActivityFeedPanelOpenButton,
} from 'components/activity-feed-panel';
import styles from './market-data.module.scss';

// Debounce a rapidly-changing value (the chart query) so live edits — typing a tenor, nudging a
// stepper — coalesce into one refetch instead of firing on every keystroke.
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export function MarketDataContent() {
  const curves = useAtomValue(curvesAtom);
  const contracts = useAtomValue(contractsAtom);
  const refreshCurves = useRefreshCurves();
  const refreshContracts = useRefreshContracts();

  // Selection lives in the URL: /pricing/market-data/:id (a saved view), .../new (an unsaved
  // draft), or the bare path (which redirects to your latest view or a new draft). This makes
  // views deep-linkable and makes browser Back/Forward step through them.
  const { id: routeId } = useParams<{ id?: string }>();
  const history = useHistory();
  const isNew = routeId === 'new';
  const selectedId = routeId && routeId !== 'new' ? Number(routeId) : null;
  const viewPath = (vid: number) =>
    routes.pricingMarketDataViewPath.replace(':id', String(vid));
  const NEW_PATH = routes.pricingMarketDataViewPath.replace(':id', 'new');
  const BASE_PATH = routes.pricingMarketDataPath;

  const [asOfDates, setAsOfDates] = useState<string[]>([]);
  const [saved, setSaved] = useState<SavedView[]>([]);
  const [draft, setDraft] = useState<ViewDraft | null>(null);
  const [baseline, setBaseline] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [toast, setToast] = useState<PricingToast | null>(null);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [openSeriesId, setOpenSeriesId] = useState<number | null>(null);

  // What the draft currently holds ("id-<n>" or "new"), so the URL→draft sync doesn't reload a
  // view that's already open (which would clobber in-progress edits on an unrelated refresh).
  const loadedKeyRef = useRef<string | null>(null);
  // A duplicate stashes its prefilled draft here to survive the navigation to .../new.
  const pendingDraftRef = useRef<ViewDraft | null>(null);
  // After a save, defer the URL change to /:id until `dirty` clears, so the navigation guard
  // doesn't treat the just-saved view as unsaved.
  const [pendingSavedId, setPendingSavedId] = useState<number | null>(null);

  const selectedView = saved.find((v) => v.id === selectedId) ?? null;
  // Someone else's view is read-only: you can look at its chart and duplicate it, but not
  // edit, save over, or delete it.
  const readOnly = selectedView !== null && selectedView.mine === false;
  const dirty = draft && !readOnly ? snapshot(draft) !== baseline : false;
  const hasInstruments = curves.length > 0 || contracts.length > 0;

  // Save gate: a view can't be saved while it still carries the default name, is blank, or
  // collides (case-insensitively) with another of MY OWN saved views (names are unique per
  // user, not globally). The currently selected view is excluded so re-saving an open view
  // isn't flagged as a self-collision. Read-only (someone else's) views can't be saved at all.
  const trimmedName = draft?.name.trim() ?? '';
  const nameIsDefault = trimmedName === '' || trimmedName === DEFAULT_VIEW_NAME;
  const nameCollision =
    draft !== null &&
    saved.some(
      (v) =>
        v.mine &&
        v.id !== selectedId &&
        v.name.trim().toLowerCase() === trimmedName.toLowerCase()
    );
  const nameError = nameIsDefault
    ? 'Rename this view before saving.'
    : nameCollision
      ? 'You already have a view with this name.'
      : '';
  const canSave = draft !== null && !readOnly && !saving && nameError === '';

  // How a view's name renders wherever it's shown (page + dropdown): bare when it's mine,
  // "Name -- Owner" for everyone else's.
  const viewLabel = (v: SavedView) => {
    return v.mine ? v.name : `${v.name} -- ${v.ownerName ?? 'Unknown'}`;
  };
  // In the grouped "All views" section, even my own views are attributed by owner so related
  // names cluster together.
  const attributedLabel = (v: SavedView) =>
    `${v.name} -- ${v.ownerName ?? 'Unknown'}`;

  // The combobox lists every view (alphabetical by "Name -- Owner" so same-named views across
  // users sit together); each is attributed to its owner.
  const allViews = saved
    .slice()
    .sort((a, b) => attributedLabel(a).localeCompare(attributedLabel(b)));

  const refreshViews = useCallback(
    () => pricingApi.listSavedViews().then((r) => setSaved(r.results)),
    []
  );

  useEffect(() => {
    Promise.all([
      pricingApi.asOfDates(),
      refreshCurves(),
      refreshContracts(),
      pricingApi.listSavedViews(),
    ])
      .then(([dates, , , views]) => {
        setAsOfDates(dates.results);
        setSaved(views.results);
      })
      .catch((e: Error) => setToast({ type: 'error', message: e.message }))
      .finally(() => setLoaded(true));
  }, [refreshCurves, refreshContracts]);

  // ── live market-data query (react-query) ─────────────────────────
  // The chart derives from the CURRENT draft: buildQuery → useQuery keyed on that query, so any
  // series/settings edit refetches (debounced) and re-plots — no explicit "Apply" needed. A draft
  // with no instrument-bearing series yields an empty query; we skip the fetch and prompt instead.
  const liveQuery = useMemo(
    () => (draft ? buildQuery(draft, asOfDates) : null),
    [draft, asOfDates]
  );
  const liveRenderable = !!liveQuery && liveQuery.series.length > 0;
  const queryKey = liveRenderable ? JSON.stringify(liveQuery) : '';
  const debouncedKey = useDebouncedValue(queryKey, 350);
  const debouncedQuery = useMemo(
    () => (debouncedKey ? (JSON.parse(debouncedKey) as MarketDataQuery) : null),
    [debouncedKey]
  );
  const marketQuery = useQuery({
    queryKey: ['pricing', 'market-data', debouncedKey],
    queryFn: () => pricingApi.marketData(debouncedQuery as MarketDataQuery),
    enabled: loaded && asOfDates.length > 0 && !!debouncedQuery,
    placeholderData: keepPreviousData,
  });

  // Build the plot from the fetched series + the draft's per-series presentation (name/colour/risk)
  // so a colour or name change re-plots without a refetch. Null while the draft is unplottable.
  const plot = useMemo<PricePlot | null>(() => {
    if (!liveRenderable || !draft || !marketQuery.data) return null;
    const meta = new Map<number | string, SeriesMeta>(
      draft.series.map((s) => [
        s.id,
        { name: s.name, color: s.color, addRisk: s.addRisk },
      ])
    );
    return buildPricePlot(
      marketQuery.data.series,
      meta,
      resolveBase(draft, asOfDates)
    );
  }, [liveRenderable, draft, marketQuery.data, asOfDates]);

  // ── view navigation (guarded) ────────────────────────────────────
  const blankDraft = useCallback(
    (): ViewDraft => ({
      name: DEFAULT_VIEW_NAME,
      asOfMode: 'latest',
      baseAsOf: asOfDates[0] ?? '',
      historical: DEFAULT_HISTORICAL,
      horizon: DEFAULT_HORIZON,
      series: [blankSeries(asOfDates[0] ?? '')],
    }),
    [asOfDates]
  );

  const startBlank = useCallback(() => {
    const d = blankDraft();
    setDraft(d);
    setBaseline(snapshot(d));
  }, [blankDraft]);

  const openView = useCallback(
    (view: SavedView) => {
      const cfg = view.config;
      const series = ((cfg.series as VizSeries[] | undefined) ?? []).map(
        (s) => ({
          ...s,
          id: nextId(),
        })
      );
      const d: ViewDraft = {
        name: view.name,
        // Back-compat: older views stored only baseAsOf + a string horizon, no mode/historical.
        asOfMode: cfg.asOfMode ?? (cfg.baseAsOf ? 'fixed' : 'latest'),
        baseAsOf: cfg.baseAsOf ?? asOfDates[0] ?? '',
        historical: cfg.historical ?? DEFAULT_HISTORICAL,
        horizon: Number(cfg.horizon) || DEFAULT_HORIZON,
        series,
      };
      setDraft(d);
      setBaseline(snapshot(d));
    },
    [asOfDates]
  );

  // Navigation is pure — it only changes the URL. The effect below reconciles the draft to it,
  // and the NavigationGuard prompts before leaving unsaved changes (covering Back/Forward too).
  const newView = () => history.push(NEW_PATH);
  const selectView = (vid: number | null) =>
    history.push(vid === null ? NEW_PATH : viewPath(vid));

  // URL → draft reconciliation. Loads the view named in the URL, seeds a blank/duplicate draft
  // for .../new, and redirects the bare path to the latest own view (or a new draft).
  useEffect(() => {
    if (!loaded || asOfDates.length === 0) return;
    if (selectedId === null && !isNew) {
      const mine = saved.filter((v) => v.mine);
      history.replace(
        mine.length ? viewPath(mine[mine.length - 1].id) : NEW_PATH
      );
      return;
    }
    if (isNew) {
      if (loadedKeyRef.current !== 'new') {
        const pending = pendingDraftRef.current;
        if (pending) {
          pendingDraftRef.current = null;
          setDraft(pending);
          setBaseline(''); // a duplicate is unsaved → dirty until the user saves it
        } else {
          startBlank();
        }
        loadedKeyRef.current = 'new';
      }
      return;
    }
    const v = saved.find((x) => x.id === selectedId);
    if (!v) {
      history.replace(BASE_PATH); // unknown/deleted id → back to seed
      return;
    }
    if (loadedKeyRef.current !== `id-${selectedId}`) {
      openView(v);
      loadedKeyRef.current = `id-${selectedId}`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, asOfDates, selectedId, isNew, saved, openView, startBlank]);

  // After a save, redirect to the new view's URL once `dirty` has cleared (so the guard allows it).
  useEffect(() => {
    if (pendingSavedId !== null && !dirty) {
      const target = viewPath(pendingSavedId);
      loadedKeyRef.current = `id-${pendingSavedId}`;
      setPendingSavedId(null);
      history.replace(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingSavedId, dirty]);

  // ── draft mutations ──────────────────────────────────────────────
  const patch = (p: Partial<ViewDraft>) =>
    setDraft((d) => {
      if (!d) return d;
      return { ...d, ...p };
    });
  const update = (id: number, p: Partial<VizSeries>) =>
    setDraft((d) => {
      if (!d) return d;
      return {
        ...d,
        series: d.series.map((s) => {
          if (s.id !== id) return s;
          return { ...s, ...p };
        }),
      };
    });
  const addSeries = () =>
    setDraft((d) => {
      if (!d) return d;
      const s = blankSeries(d.baseAsOf);
      s.name = `Series ${d.series.length + 1}`;
      s.color = DEFAULT_COLORS[d.series.length % DEFAULT_COLORS.length];
      setOpenSeriesId(s.id);
      return { ...d, series: [...d.series, s] };
    });
  const removeSeries = (id: number) =>
    setDraft((d) => {
      if (!d) return d;
      return { ...d, series: d.series.filter((s) => s.id !== id) };
    });

  // Duplicate the current view into a NEW unsaved draft owned by me. From my own view it's a
  // plain "(copy)"; from someone else's read-only view it "Duplicate into my views", keeping the
  // name (the save-gate will prompt a rename if I already have one by that name).
  const duplicateView = () => {
    if (!draft) return;
    // Stash the copy so it survives the navigation to .../new (see the URL→draft effect).
    pendingDraftRef.current = {
      ...draft,
      name: readOnly ? draft.name : `${draft.name} (copy)`,
      series: draft.series.map((s) => ({ ...s, id: nextId() })),
    };
    setToast({
      type: 'success',
      message: readOnly
        ? 'Copied into your views — rename if needed, then Save'
        : 'Duplicated — rename if needed, then Save',
    });
    history.push(NEW_PATH);
  };

  // ── save / delete ────────────────────────────────────────────────
  const persist = async (name: string, d: ViewDraft) => {
    const sv = await pricingApi.saveView(name, {
      series: d.series,
      asOfMode: d.asOfMode,
      baseAsOf: d.baseAsOf,
      historical: d.historical,
      horizon: d.horizon,
    });
    await refreshViews();
    const next = { ...d, name };
    setDraft(next);
    setBaseline(snapshot(next)); // now clean…
    setPendingSavedId(sv.id); // …so the deferred effect can move the URL to /:id
    setToast({ type: 'success', message: `View "${name}" saved` });
  };

  // Persist the current view. The chart already renders live from the draft, so this only saves —
  // the name comes straight from the inline "View name" field, no prompt.
  const save = async () => {
    if (!draft || !canSave) return;
    setSaving(true);
    try {
      await persist(draft.name.trim(), draft);
    } catch (e) {
      setToast({ type: 'error', message: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const delId = deleteId;
    setDeleteId(null);
    if (delId === null) return;
    try {
      await pricingApi.deleteSavedView(delId);
      const r = await pricingApi.listSavedViews();
      setSaved(r.results);
      if (selectedId === delId) {
        loadedKeyRef.current = null;
        history.replace(BASE_PATH); // → the URL→draft effect reseeds to another view / new
      }
      setToast({ type: 'success', message: 'View deleted' });
    } catch (e) {
      setToast({ type: 'error', message: (e as Error).message });
    }
  };

  const exportCsv = () => {
    if (!plot) return;
    const rows: string[][] = [['series', 'date', 'value']];
    plot.lines.forEach((ln) =>
      ln.points.forEach(([x, y]) =>
        rows.push([ln.key, new Date(x).toISOString().slice(0, 10), String(y)])
      )
    );
    const csv = rows
      .map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(draft?.name || 'view')
      .replace(/\s+/g, '-')
      .toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const legend = useMemo(
    () =>
      (draft?.series ?? []).map((s) => ({
        id: s.id,
        name: s.name,
        color: s.color,
      })),
    [draft?.series]
  );
  // ── config rail (nav slot) ───────────────────────────────────────
  const rail = (
    <div className={styles.configRail}>
      <div className={styles.railHead}>
        <div className={styles.railTitle}>
          {isNew ? 'New view' : 'View selector'}
        </div>
        <div className={styles.railActions}>
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={Save}
            iconDescription={saving ? 'Saving…' : 'Save view'}
            tooltipPosition="bottom"
            tooltipAlignment="end"
            onClick={save}
            disabled={!canSave}
          />
        </div>
      </div>

      <Button
        kind="ghost"
        size="sm"
        renderIcon={Add}
        className={styles.newViewBtn}
        onClick={newView}
      >
        New view
      </Button>

      {saved.length > 0 && (
        <ComboBox
          id="pv-view"
          size="sm"
          titleText="Saved views"
          placeholder="Select a view — search by name…"
          items={allViews}
          selectedItem={selectedView}
          itemToString={(v) => (v ? attributedLabel(v) : '')}
          itemToElement={(v) => {
            if (!v) return <span />;
            const meta = [
              v.createdAt ? `Created ${fmtDate(v.createdAt)}` : null,
              v.ownerName,
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <span className={styles.viewOption}>
                <span className={styles.viewOptionName}>{v.name}</span>
                <span className={styles.viewOptionMeta}>{meta}</span>
              </span>
            );
          }}
          onChange={({ selectedItem }) =>
            selectView(selectedItem ? selectedItem.id : null)
          }
        />
      )}

      {draft && dirty && selectedId !== null && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title="Unsaved changes"
          subtitle="Apply/Save to persist."
          className={styles.unsaved}
        />
      )}

      {draft && (
        <>
          {/* Name + global window in a collapsible section. The disabled fieldset makes the
              controls inert when viewing someone else's (read-only) view; `display: contents`
              keeps it layout-neutral. */}
          <Accordion className={styles.nameAccordion}>
            <AccordionItem title="Name & Global Horizon" open>
              <fieldset className={styles.configFieldset} disabled={readOnly}>
                <TextInput
                  id="pv-name"
                  size="sm"
                  labelText="View name"
                  value={draft.name}
                  onChange={(e) => patch({ name: e.target.value })}
                  invalid={nameError !== ''}
                  invalidText={nameError}
                />

                <div className={styles.windowBox}>
                  <span className={styles.windowBoxLabel}>Global window</span>
                  <Select
                    id="pv-asof-mode"
                    size="sm"
                    labelText="Base as-of"
                    value={draft.asOfMode}
                    onChange={(e) =>
                      patch({ asOfMode: e.target.value as AsOfMode })
                    }
                  >
                    <SelectItem value="latest" text="Latest" />
                    <SelectItem value="fixed" text="Fixed date" />
                  </Select>
                  {draft.asOfMode === 'fixed' && (
                    <DatePicker
                      datePickerType="single"
                      dateFormat="Y-m-d"
                      enable={asOfDates}
                      value={draft.baseAsOf}
                      onChange={(_dates: Date[], dateStr: string) =>
                        patch({ baseAsOf: dateStr })
                      }
                    >
                      <DatePickerInput
                        id="pv-base-asof"
                        size="sm"
                        labelText="Fixed as-of date"
                        placeholder="yyyy-mm-dd"
                      />
                    </DatePicker>
                  )}
                  <div className={styles.grid2}>
                    <NumberInput
                      id="pv-historical"
                      size="sm"
                      label="Historical (months)"
                      min={0}
                      step={1}
                      value={draft.historical}
                      onChange={(_e, { value }) =>
                        patch({ historical: Math.max(0, Number(value) || 0) })
                      }
                    />
                    <NumberInput
                      id="pv-horizon"
                      size="sm"
                      label="Horizon (months)"
                      min={0}
                      step={1}
                      value={draft.horizon}
                      onChange={(_e, { value }) =>
                        patch({ horizon: Math.max(0, Number(value) || 0) })
                      }
                    />
                  </div>
                </div>
              </fieldset>
            </AccordionItem>
          </Accordion>

          <div className={styles.seriesHeader}>
            <span className={styles.seriesHeaderTitle}>
              Series in this view
            </span>
            <Button
              kind="ghost"
              size="sm"
              hasIconOnly
              renderIcon={Add}
              iconDescription="Add series"
              tooltipPosition="bottom"
              tooltipAlignment="end"
              onClick={addSeries}
              disabled={readOnly || !hasInstruments}
            />
          </div>

          <Accordion className={styles.seriesAccordion}>
            {draft.series.map((s) => (
              <AccordionItem
                key={s.id}
                title={
                  <span className={styles.seriesTitle}>
                    <span
                      className={styles.seriesDot}
                      style={{ background: s.color }}
                    />
                    {s.name}
                  </span>
                }
                open={openSeriesId === s.id}
                onHeadingClick={() =>
                  setOpenSeriesId((cur) => (cur === s.id ? null : s.id))
                }
              >
                <SeriesEditor
                  series={s}
                  readOnly={readOnly}
                  curves={curves}
                  contracts={contracts}
                  asOfDates={asOfDates}
                  onChange={(p) => update(s.id, p)}
                  onRemove={() => removeSeries(s.id)}
                />
              </AccordionItem>
            ))}
          </Accordion>
        </>
      )}
    </div>
  );

  // Record-level actions in the top PageHeader, matching the other pricing detail pages
  // (full-text Delete / Duplicate). Apply/Save stays in the rail with the view editor.
  const pageActions = draft ? (
    <div className={styles.headerActions}>
      <Button
        kind="danger--tertiary"
        size="md"
        renderIcon={TrashCan}
        disabled={selectedId === null || readOnly}
        onClick={() => setDeleteId(selectedId)}
      >
        Delete
      </Button>
      <Button
        kind="tertiary"
        size="md"
        renderIcon={Copy}
        disabled={selectedView === null}
        onClick={duplicateView}
      >
        {readOnly ? 'Duplicate into my views' : 'Duplicate'}
      </Button>
      <OverflowMenu aria-label="More view actions" size="md" flipped>
        <OverflowMenuItem
          itemText="Export CSV"
          disabled={!plot || plot.lines.length === 0}
          onClick={exportCsv}
        />
      </OverflowMenu>
      {selectedId !== null && (
        <>
          <span className={styles.divider} aria-hidden />
          <ActivityFeedPanelOpenButton />
        </>
      )}
    </div>
  ) : null;

  return (
    <PricingPageLayout
      nav={rail}
      pageTitle="Price Visualization"
      pageActions={pageActions}
      toast={toast}
      onToastClose={() => setToast(null)}
    >
      {draft ? (
        <div className={styles.detail}>
          <div className={styles.graphPanel}>
            <div className={styles.chartCap}>
              <h2>Price by date</h2>
              <p>Settles month-end · forward strips dashed</p>
            </div>
            {plot && plot.lines.length > 0 ? (
              <>
                <PriceChart plot={plot} />
                <div className={styles.legend}>
                  {legend.map((li) => (
                    <span key={li.id} className={styles.legendItem}>
                      <span
                        className={styles.legendDot}
                        style={{ background: li.color }}
                      />
                      {li.name}
                    </span>
                  ))}
                  <span className={styles.legendNote}>
                    Dashed = forward strip projected from each as-of date
                  </span>
                </div>
              </>
            ) : (
              <div className={styles.chartEmpty}>
                {!liveRenderable ? (
                  <span>
                    Select an <strong>instrument</strong> for a series to plot
                    it.
                  </span>
                ) : marketQuery.isError ? (
                  `Couldn't load market data: ${
                    (marketQuery.error as Error).message
                  }`
                ) : marketQuery.isPending ? (
                  'Loading…'
                ) : (
                  'No data for the current selection.'
                )}
              </div>
            )}
            <footer className={styles.source}>
              Source: Stronghold market-data warehouse. Settles are month-end
              fixings, each projecting its own dashed forward strip.{' '}
              <strong>Confidential.</strong>
            </footer>
          </div>

          <Accordion className={styles.detailsAccordion}>
            <AccordionItem title="Price Visualization Details">
              <dl className={styles.detailGrid}>
                <Field
                  label="Name"
                  value={
                    (selectedView ? viewLabel(selectedView) : draft.name) || '—'
                  }
                />
                <Field
                  label="Base as-of"
                  value={
                    draft.asOfMode === 'fixed'
                      ? draft.baseAsOf || '—'
                      : `Latest (${resolveBase(draft, asOfDates) || '—'})`
                  }
                />
                <Field
                  label="Window"
                  value={`${draft.historical} mo back – ${
                    draft.horizon
                  } mo fwd${
                    resolveBase(draft, asOfDates)
                      ? ` (to ${horizonEnd(
                          resolveBase(draft, asOfDates),
                          draft.horizon
                        )})`
                      : ''
                  }`}
                />
                <Field
                  label="Created by"
                  value={selectedView?.createdBy || '—'}
                />
                <Field
                  label="Created At"
                  value={fmtDate(selectedView?.createdAt)}
                />
                <Field
                  label="Updated by"
                  value={selectedView?.updatedBy || '—'}
                />
                <Field
                  label="Updated At"
                  value={fmtDate(selectedView?.updatedAt)}
                />
              </dl>
            </AccordionItem>
          </Accordion>
        </div>
      ) : (
        <EmptyState
          size="lg"
          title="No visuals yet"
          subtitle="Create your first view to start plotting series across a horizon."
          action={{ text: 'New view', onClick: newView }}
        />
      )}

      {selectedId !== null && (
        <ActivityFeedPanel
          modelType="Pricing::SavedView"
          modelId={selectedId}
          title="Activity"
          subtitle={selectedView?.name || 'View'}
          commentsHref={`/pricing/saved_views/${selectedId}/comments`}
        />
      )}

      <Modal
        open={deleteId !== null}
        danger
        size="sm"
        modalHeading="Delete view?"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onRequestSubmit={confirmDelete}
        onRequestClose={() => setDeleteId(null)}
      >
        This permanently removes the saved view.
      </Modal>

      <NavigationGuard
        hasChanges={dirty}
        message="This view has unsaved changes that will be lost. Discard them?"
      />
    </PricingPageLayout>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.detailItem}>
      <dt className={styles.detailLabel}>{label}</dt>
      <dd className={styles.detailValue}>{value}</dd>
    </div>
  );
}
